/**
 * TagSuggestionsSection — Scored, explainable place suggestions for Experience curation.
 * Calls get_experience_place_suggestions RPC.
 * Displays score, confidence badge, match reasons, and missing reasons.
 * Supports filtering to strong matches only.
 *
 * Scoring model (canonical, documented in docs/matching/matching-model-v1.md):
 *   +30 per required (AND) intent match
 *   +20 per optional (OR) intent match
 *   +10 if place is published
 *   +5 if place has media
 * Confidence: score >= 50 → strong, >= 25 → medium, else weak
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { EXPLORE_DEBUG } from '@/lib/debug';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus, Lightbulb, Filter, ChevronDown, ChevronUp } from 'lucide-react';
import { useCoverImageUrl } from '@/hooks/useMediaAssets';
import { INTENT_CONFIG } from '@/lib/placecore/public/mappings';
import { cn } from '@/lib/utils';

interface Suggestion {
  place_id: string;
  slug: string;
  name: string;
  municipality: string | null;
  landskap: string | null;
  status: string;
  match_score: number;
  confidence: 'strong' | 'medium' | 'weak';
  matched_tags: string[];
  match_reasons: string[];
  missing_reasons: string[];
}

interface TagSuggestionsSectionProps {
  entitySlug: string;
  linkedPlaceIds: Set<string>;
  onAdd: (placeId: string) => void;
  addPending?: boolean;
  tagLabels?: Record<string, string>;
}

const INTENT_LABEL_MAP: Record<string, string> = Object.fromEntries(
  Object.entries(INTENT_CONFIG).map(([key, cfg]) => [key, cfg.label])
);

const CONFIDENCE_CONFIG = {
  strong: { label: 'Stark', className: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800' },
  medium: { label: 'Medel', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200 dark:border-amber-800' },
  weak: { label: 'Svag', className: 'bg-muted text-muted-foreground border-border' },
} as const;

export function TagSuggestionsSection({
  entitySlug,
  linkedPlaceIds,
  onAdd,
  addPending = false,
  tagLabels,
}: TagSuggestionsSectionProps) {
  const [strongOnly, setStrongOnly] = useState(false);

  const { data: suggestions = [], isLoading } = useQuery({
    queryKey: ['tag-suggestions', 'experience', entitySlug],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_experience_place_suggestions' as any, {
        p_experience_slug: entitySlug,
        p_limit: 20,
      });
      if (error) {
        if (EXPLORE_DEBUG) console.warn('[TagSuggestions] RPC error:', error.message);
        throw error;
      }
      if (EXPLORE_DEBUG) {
        console.log(`[TagSuggestions] get_experience_place_suggestions(${entitySlug}) →`, data);
      }
      return (data ?? []) as unknown as Suggestion[];
    },
    staleTime: 10_000,
  });

  const filtered = suggestions.filter((s) => {
    if (linkedPlaceIds.has(s.place_id)) return false;
    if (strongOnly && s.confidence !== 'strong') return false;
    return true;
  });

  const strongCount = suggestions.filter((s) => !linkedPlaceIds.has(s.place_id) && s.confidence === 'strong').length;
  const totalCount = suggestions.filter((s) => !linkedPlaceIds.has(s.place_id)).length;

  const getLabel = (slug: string) =>
    tagLabels?.[slug] ?? INTENT_LABEL_MAP[slug] ?? slug;

  if (isLoading) {
    return (
      <div className="mt-6 pt-5 border-t border-dashed">
        <div className="flex items-center gap-2 mb-3">
          <Lightbulb className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-medium text-muted-foreground">Förslag (baserat på taggar)</h3>
        </div>
        <div className="space-y-1.5">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 rounded-lg bg-muted animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6 pt-5 border-t border-dashed">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Lightbulb className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-medium text-muted-foreground">
            Förslag
            {totalCount > 0 && (
              <span className="ml-1.5 text-xs font-normal">
                ({strongOnly ? `${filtered.length} starka` : `${totalCount} st`}{strongCount > 0 && !strongOnly ? ` · ${strongCount} starka` : ''})
              </span>
            )}
          </h3>
        </div>
        {totalCount > 0 && strongCount > 0 && strongCount < totalCount && (
          <Button
            variant={strongOnly ? 'secondary' : 'ghost'}
            size="sm"
            className="h-6 text-xs gap-1 px-2"
            onClick={() => setStrongOnly(!strongOnly)}
          >
            <Filter className="h-3 w-3" />
            {strongOnly ? 'Visa alla' : 'Bara starka'}
          </Button>
        )}
      </div>

      {filtered.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">
          {suggestions.length === 0
            ? 'Inga förslag ännu — lägg till matchning via taggar'
            : strongOnly
              ? 'Inga starka matchningar'
              : 'Alla förslag redan tillagda'}
        </p>
      ) : (
        <div className="space-y-1.5">
          {filtered.map((s) => (
            <SuggestionRow
              key={s.place_id}
              suggestion={s}
              getLabel={getLabel}
              onAdd={() => onAdd(s.place_id)}
              addPending={addPending}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SuggestionRow({
  suggestion,
  getLabel,
  onAdd,
  addPending,
}: {
  suggestion: Suggestion;
  getLabel: (slug: string) => string;
  onAdd: () => void;
  addPending: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const coverUrl = useCoverImageUrl('place', suggestion.place_id, 320);
  const locationText = [suggestion.municipality, suggestion.landskap].filter(Boolean).join(', ');
  const conf = CONFIDENCE_CONFIG[suggestion.confidence] ?? CONFIDENCE_CONFIG.weak;

  const MAX_TAGS = 3;
  const visibleTags = suggestion.matched_tags.slice(0, MAX_TAGS);
  const extraCount = suggestion.matched_tags.length - MAX_TAGS;

  return (
    <div className="rounded-lg border border-dashed bg-muted/20 text-sm">
      <div className="flex items-center gap-2.5 p-2">
        {/* Thumbnail */}
        <div className="shrink-0 w-9 h-9 rounded-md overflow-hidden bg-muted flex items-center justify-center">
          {coverUrl ? (
            <img src={coverUrl} alt={suggestion.name} className="w-full h-full object-cover" />
          ) : (
            <Lightbulb className="h-3 w-3 text-muted-foreground/30" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-sm font-medium">{suggestion.name}</span>
            <Badge variant="outline" className={cn('text-[10px] px-1.5 py-0 h-4 font-medium shrink-0 border', conf.className)}>
              {conf.label}
            </Badge>
            <span className="text-[10px] text-muted-foreground shrink-0">{suggestion.match_score}p</span>
          </div>
          <div className="flex items-center gap-1 mt-0.5 flex-wrap">
            {locationText && (
              <span className="text-xs text-muted-foreground">{locationText} ·</span>
            )}
            {visibleTags.map((tag) => (
              <Badge key={tag} variant="outline" className="text-[10px] px-1.5 py-0 h-4 font-normal">
                {getLabel(tag)}
              </Badge>
            ))}
            {extraCount > 0 && (
              <span className="text-[10px] text-muted-foreground">+{extraCount}</span>
            )}
          </div>
        </div>

        {/* Actions */}
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="shrink-0 h-7 w-7 flex items-center justify-center rounded-md hover:bg-muted transition-colors"
        >
          {expanded ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />}
        </button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0"
          onClick={onAdd}
          disabled={addPending}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      {/* Expanded reasons */}
      {expanded && (
        <div className="px-3 pb-2.5 pt-0.5 space-y-1.5 border-t border-dashed mx-2 mt-0.5">
          {suggestion.match_reasons.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              <span className="text-[10px] text-muted-foreground font-medium">Matchar:</span>
              {suggestion.match_reasons.map((r, i) => (
                <span key={i} className="text-[10px] text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/30 rounded px-1.5 py-0.5">
                  {r}
                </span>
              ))}
            </div>
          )}
          {suggestion.missing_reasons.length > 0 && (
            <div className="flex flex-wrap gap-1">
              <span className="text-[10px] text-muted-foreground font-medium">Saknar:</span>
              {suggestion.missing_reasons.map((r, i) => (
                <span key={i} className="text-[10px] text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 rounded px-1.5 py-0.5">
                  {r}
                </span>
              ))}
            </div>
          )}
          {suggestion.status === 'draft' && (
            <span className="text-[10px] text-muted-foreground italic">Platsen är ej publicerad</span>
          )}
        </div>
      )}
    </div>
  );
}
